import { Link as AmplifyLink } from '@aws-amplify/ui-react';
import { useContext, useState } from 'react';
import { BiChevronDown, BiChevronUp } from 'react-icons/bi';
import { Link } from 'react-router-dom';
import { GlobalContext } from '../../global-context';
import { CompactRole } from '../../pages/person/CompactRole';
import { InstitutionEvidence, getInstitutionEvidence } from '../../pages/person/InstitutionEvidence';
import { RoleTagsSummary, sortRolesByPriority } from '../../pages/person/RoleTagsSummary';
import { FieldName, SortOptions } from '../../utils/CustomTypes';
import General from '../../utils/GeneralUtils';
import { TableFields } from '../../utils/TableFields';
import EmailAddress from '../EmailAddress';
import PhoneNumber from '../PhoneNumber';
import { LastSeenIndicator } from '../LastSeenIndicator';
import NA from '../NA';
import SortLabel from '../SortLabel';
import './TableRowContact.scss';

export function TableRowHeaderContactKeystone({
  isAllSortingDisabled = false,
  showSubheader,
  currentSort,
  tableFields,
  onSort
}: {
  isAllSortingDisabled?: boolean,
  showSubheader: boolean,
  currentSort: SortOptions,
  tableFields: TableFields,
  onSort: (sortOptions: SortOptions) => void
}) {
  if (!showSubheader) return <></>
  return <>
    <div className='results-subheader' style={{ paddingLeft: 45 }}>
      <div style={{ flex: `1 1 500px` }}>
        { tableFields.isFieldVisible(FieldName.NAME) && <span style={{ marginRight: 10 }}><SortLabel fieldName={FieldName.NAME} isDisabled={isAllSortingDisabled ? true : !tableFields.isFieldSortable(FieldName.NAME)} currentSort={currentSort} onSort={onSort} /></span> }
        { tableFields.isFieldVisible(FieldName.TITLE) && <SortLabel fieldName={FieldName.TITLE} isDisabled={isAllSortingDisabled ? true : !tableFields.isFieldSortable(FieldName.TITLE)} currentSort={currentSort} onSort={onSort} /> }
      </div>
      {
        (
          tableFields.isFieldVisible(FieldName.EMAIL) ||
          tableFields.isFieldVisible(FieldName.PHONE)
        ) && <div style={{ flex: `1 1 300px` }}>
          { tableFields.isFieldVisible(FieldName.EMAIL) && <span style={{ marginRight: 10 }}><SortLabel fieldName={FieldName.EMAIL} isDisabled={isAllSortingDisabled ? true : !tableFields.isFieldSortable(FieldName.EMAIL)} currentSort={currentSort} onSort={onSort} /></span> }
          { tableFields.isFieldVisible(FieldName.PHONE) && <span style={{ marginRight: 10 }}><SortLabel fieldName={FieldName.PHONE} isDisabled={isAllSortingDisabled ? true : !tableFields.isFieldSortable(FieldName.PHONE)} currentSort={currentSort} onSort={onSort} /></span> }
        </div>
      }
      {/* <div style={{ flex: `1 1 40px` }}></div> */}
    </div>
  </>
}

function getContactInstitutions(row: any) {
  const firstDistrict = row?.belongsTo?.find((it: any) => it?.institution?.type === 'DISTRICT')
  const firstSchool = row?.belongsTo?.find((it: any) => it?.institution?.type === 'SCHOOL')
  const firstAgency = row?.belongsTo?.find((it: any) => it?.institution?.type === 'AGENCY')

  return {
    // Fall back to school if no district/agency (e.g., private school contacts)
    district: firstDistrict?.institution || firstAgency?.institution || firstSchool?.institution,
    school: firstSchool?.institution
  }
}

export function TableRowContactKeystone({
  row,
  tableFields,
  showRowActionButtons,
  showMainInstitution = true,
  onTagContact,
  onUntagContact,
  onEditContact,
  onSelectScreenshot,
  onRowClick,
}: {
  row: any,
  tableFields: TableFields,
  showRowActionButtons: boolean,
  showMainInstitution?: boolean,
  onTagContact: (data: any) => void,
  onUntagContact: (data: any) => void,
  onEditContact: (data: any) => void,
  onSelectScreenshot: (data: any) => void,
  onRowClick?: (e: React.MouseEvent) => void,
}) {
  const globalContext = useContext(GlobalContext);

  const [isFulltextExpanded, setIsFulltextExpanded] = useState(false)
  const [isDetailExpanded, setIsDetailExpanded] = useState(false)

  const contactInstitutions = getContactInstitutions(row)


  // Handle row click - expands the row to show details
  const handleRowClick = (e: React.MouseEvent) => {
    // Don't trigger if clicking on a link or button
    const target = e.target as HTMLElement;
    if (target.tagName === 'A' || target.closest('a') || target.tagName === 'BUTTON' || target.closest('button') || target.closest('.tiny-button') || target.closest('.ignore-click')) {
      return;
    }
    setIsDetailExpanded(!isDetailExpanded);
  };

  return <>
    <div className='results-row-content' onClick={handleRowClick}>
      <div className='results-row-main-row' style={{ display: 'flex', flexWrap: 'nowrap' }}>
        <div style={{ flex: '1 1 543px', minWidth: 0 }}>
          <div className='results-row-main-line'>
            <Link className='main-table-link' to={`/directory/${globalContext.environment.entities.contact.urlSegment}/${row.id}`}>
              { General.trim(row.name, 50) || <NA /> }
            </Link>
            {tableFields.isFieldVisible(FieldName.TITLE) && row.title && (
              <span className='main-table-title-inline' title={row.title}>
                {row.title}
                {row.titleHistory && Array.isArray(row.titleHistory) && row.titleHistory.length > 1 && (
                  <span className="title-history-more"> +{row.titleHistory.length - 1} more</span>
                )}
              </span>
            )}
          </div>
          {row?.roles?.length > 0 && (
            <div className='results-row-role-tags'>
              <RoleTagsSummary roles={row?.roles} />
            </div>
          )}
          {/* {
            tableFields.isFieldVisible(FieldName.BIO) && row?.bios?.length > 0 &&
              <div className='desc-9'>{ row?.bios[0]?.bio }</div>
          } */}
          {
            showMainInstitution && <div className="contact-institution-row">
              {contactInstitutions?.district && (
                <Link className='silent' to={`/directory/institutions/${contactInstitutions.district.id || ''}`}>
                  {contactInstitutions.district.name}{contactInstitutions.district.loc_state ? `, ${contactInstitutions.district.loc_state}` : ''}
                </Link>
              )}
            </div>
          }
          {
            row?.fulltextMatchedUrls?.length ?
              <div>
                <span
                  className='tiny-button'
                  onClick={() => {
                    setIsFulltextExpanded(!isFulltextExpanded)
                  }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}
                >
                  { row?.fulltextMatchedUrls?.length } URL{ row?.fulltextMatchedUrls?.length === 1 ? '' : 's' } matching fulltext search { isFulltextExpanded ? <BiChevronUp /> : <BiChevronDown /> }
                </span>
              </div> : <></>
          }
        </div>
        <div style={{
          flex: '1 1 280px',
          wordBreak: 'break-all',
          overflowWrap: 'break-word',
          minWidth: 0,
          fontSize: 15
        }}>
          {
            tableFields.isFieldVisible(FieldName.EMAIL) && row['email'] &&
              <div style={{ marginBottom: 5 }}>
                <EmailAddress
                  email={row['email']}
                  emailDomainCount={row['emailDomainCount']}
                  emailVerificationStatus={row['emailVerificationStatus']}
                  emailLastVerifiedAt={row['emailLastVerifiedAt']}
                />
              </div>
          }
          {
            tableFields.isFieldVisible(FieldName.PHONE) &&
              <div>
                <PhoneNumber
                  phone={row['phone']}
                  phoneUniqueness={row['phoneUniqueness']}
                />
              </div>
          }
        </div>
        <div style={{ flex: `0 0 60px`, display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start', paddingTop: 5, minWidth: 0 }}>
          <LastSeenIndicator lastSeenAt={row.attributionMostRecent} />
        </div>
      </div>
      <div>
        {
          isFulltextExpanded ?
            <div style={{ marginTop: 10, overflow: 'hidden' }}>
              {
                row?.fulltextMatchedUrls.map((match: { url: string; snippet: string }) => (
                  <div key={match.url} className='fulltext-url-item'>
                    <AmplifyLink
                      target='_blank'
                      href={match.url}
                    >
                      {General.ellipsisInMiddle(match.url, 110)}
                    </AmplifyLink>
                    {match.snippet && (
                      <div
                        className='fulltext-snippet'
                        dangerouslySetInnerHTML={{ __html: `...${match.snippet}...` }}
                      />
                    )}
                  </div>
                ))
              }
            </div> : <></>
        }
        {
          isDetailExpanded && row?.roles?.length > 0 ?
            <div style={{ marginTop: 10, padding: '10px 0' }}>
              <div className="compact-roles-header">Roles ({row.roles.length})</div>
              {sortRolesByPriority(row.roles).map((role: any, index: number) => (
                <CompactRole key={index} role={role} onSelectScreenshot={onSelectScreenshot} />
              ))}
            </div> : <></>
        }
        {
          isDetailExpanded && row?.belongsTo?.length > 0 && (
            <InstitutionEvidence
              belongsTo={row.belongsTo}
              firstName={row.firstName}
              institutionEvidence={getInstitutionEvidence(row)}
              onSelectScreenshot={onSelectScreenshot}
            />
          )
        }
      </div>
    </div>
  </>
}
